import { useState, useCallback, RefObject } from 'react';

export type ShareChipPosition = {
  index: number;
};

interface UseShareChipsNavigationProps {
  containerRef: RefObject<HTMLDivElement>;
  shareChips: Array<{ email: string }>;
  onRemove: (email: string) => void;
  debug?: boolean;
}

interface UseShareChipsNavigationReturn {
  focusedChip: ShareChipPosition | null;
  handleChipFocus: (index: number) => void;
  handleChipKeyDown: (e: React.KeyboardEvent, index: number) => void;
  handleInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export function useShareChipsNavigation({
  containerRef,
  shareChips,
  onRemove,
  debug = false,
}: UseShareChipsNavigationProps): UseShareChipsNavigationReturn {
  const [focusedChip, setFocusedChip] = useState<ShareChipPosition | null>(null);

  const logDebug = useCallback(
    (action: string, details?: unknown) => {
      if (debug) {
        console.log(`[ShareChipsNavigation] ${action}`, details);
      }
    },
    [debug],
  );

  const handleChipFocus = useCallback(
    (index: number) => {
      setFocusedChip({ index });
      logDebug('Chip Focus', { index });
    },
    [logDebug],
  );

  const focusChip = useCallback(
    (index: number) => {
      const chipElement = containerRef.current?.querySelector(
        `[data-chip-index="${index}"]`,
      ) as HTMLElement;
      chipElement?.focus();
    },
    [containerRef],
  );

  const focusInput = useCallback(() => {
    const input = containerRef.current?.querySelector('input') as HTMLElement;
    input?.focus();
  }, [containerRef]);

  const handleChipKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number) => {
      logDebug('Chip KeyDown', { key: e.key, index });

      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          if (index > 0) {
            focusChip(index - 1);
          }
          break;

        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          if (index < shareChips.length - 1) {
            focusChip(index + 1);
          } else {
            focusInput();
          }
          break;

        case 'Delete':
        case 'Backspace':
          e.preventDefault();
          onRemove(shareChips[index].email);
          if (index > 0) {
            focusChip(index - 1);
          } else {
            focusInput();
          }
          break;
      }
    },
    [shareChips, focusChip, focusInput, logDebug, onRemove],
  );

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      logDebug('Input KeyDown', { key: e.key, value: e.currentTarget.value });

      const value = e.currentTarget.value.trim();

      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        if (value) {
          // Let parent component handle adding the email
          e.currentTarget.value = '';
        }
      } else if ((e.key === 'Backspace' || e.key === 'Delete') && !value && shareChips.length > 0) {
        e.preventDefault();
        focusChip(shareChips.length - 1);
      } else if (
        (e.key === 'ArrowLeft' || e.key === 'ArrowUp') &&
        !value &&
        shareChips.length > 0
      ) {
        e.preventDefault();
        focusChip(shareChips.length - 1);
      }
    },
    [shareChips.length, focusChip, logDebug],
  );

  return {
    focusedChip,
    handleChipFocus,
    handleChipKeyDown,
    handleInputKeyDown,
  };
}
